import { useQuery } from '@apollo/client';
import { TableColumnsType } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import {
  FETCH_WORKAROUNDS_OVERVIEW,
  GetWorkaroundOverviewResponse,
  WorkaroundApplicationOverview,
} from '../enterprise-applications';
import { tableSortingMethods } from '../../../utils';
import { TierBadge } from '../../dataInputs/tiers/components';
import FinancialData from '../../../components/common/FinancialData';
import { CompletionPercentage, ITSection } from '../components';
import TitleUpdater from '../../../components/common/TitleUpdater';
import { Footer, TitleBar } from '../../../components';
import QueryErrorBoundary from '../../../components/error-handling';
import TableV3 from '../../../components/generic/Table/TableV3';
import { locationLevelWorkaroundsCards } from '../constants';
import { useNavigate, useParams } from 'react-router';
import { useLocationName } from '../../../hooks';
import { renderComplianceBadges } from '../utils/complianceBadgeRenderer';
import { ComplianceStatusEnum } from '../../../shared/types/business-functions-workarounds';

const LocationLevelApplicationWorkarounds = () => {
  const { locationId } = useParams();
  const { locationName } = useLocationName(locationId);
  const navigate = useNavigate();

  const {
    data: workaroundsData,
    loading: workaroundsLoading,
    error: workaroundsError,
  } = useQuery<GetWorkaroundOverviewResponse>(FETCH_WORKAROUNDS_OVERVIEW, {
    variables: { locationUUID: locationId },
  });

  const cardsData = workaroundsData
    ? [
        `${
          workaroundsData.getWorkaroundOverview.totalProcessAreasWorkarounds ??
          0
        }`,
        `${
          workaroundsData.getWorkaroundOverview
            .totalBusinessFunctionsWorkarounds ?? 0
        }`,
        `${
          workaroundsData.getWorkaroundOverview
            .totalQualitativeFactorsWorkarounds ?? 0
        }`,
      ]
    : [];

  const tableData = workaroundsData?.getWorkaroundOverview.applications ?? [];

  const applicationText = `${tableData.length} Applications`;

  const columns: TableColumnsType<WorkaroundApplicationOverview> = [
    {
      title: 'Applications',
      width: 250,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (application) => (
        <span className="custom-table--column ml-10">{application}</span>
      ),
    },
    {
      title: 'Tier',
      width: 120,
      dataIndex: 'tier',
      key: 'tier',
      align: 'center',
      sorter: (a, b) => tableSortingMethods.string(a.tier?.name, b.tier?.name),
      render: (tier) => {
        const { name, color } = tier ?? {};
        return tier ? (
          <div className="flex justify-center">
            <TierBadge name={name} color={color} />
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        );
      },
    },
    {
      title: (
        <span>
          Annual Revenue <br />
          Supported
        </span>
      ),
      width: 120,
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.revenue, b.revenue),
      render: (revenue) => (
        <span className="custom-table--column">
          <FinancialData text={revenue} formatType="decimal" />
        </span>
      ),
    },
    {
      title: (
        <span>
          Business Functions <br />
          Supported
        </span>
      ),
      width: 120,
      dataIndex: 'businessFunctionCount',
      key: 'businessFunctionCount',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.businessFunctionCount,
          b.businessFunctionCount,
        ),
      render: (businessFunctionsCount) => (
        <span className="custom-table--column">{businessFunctionsCount}</span>
      ),
    },
    {
      title: (
        <span>
          Workaround <br />
          Completion
        </span>
      ),
      width: 120,
      dataIndex: 'completionPercentage',
      key: 'completionPercentage',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.completionPercentage,
          b.completionPercentage,
        ),
      render: (workaroundCompletion) => {
        return <CompletionPercentage percent={workaroundCompletion} colorize />;
      },
    },
    {
      title: (
        <span>
          Workaround <br />
          Timeline Compliance
        </span>
      ),
      width: 250,
      dataIndex: 'workaroundTimelineCompliance',
      key: 'workaroundTimelineCompliance',
      align: 'center',
      render: (complianceData: Record<string, ComplianceStatusEnum>) => {
        return renderComplianceBadges(complianceData, {
          isWorkaroundNeeded: true,
        });
      },
    },
  ];

  const cards = locationLevelWorkaroundsCards?.map((card, index) => (
    <ITSection
      key={card.id}
      overview={cardsData[index]}
      {...card}
      loading={workaroundsLoading}
      reviewLink={card.reviewLink(locationId ?? '')}
      locationId={locationId ?? ''}
      isEnabled={true} // Bypass feature flag for now
    />
  ));

  const extendTableOptions = (
    <div className="w-full text-sm ml-3 lg:ml-0 lg:text-base font-bold text-primary-200 min-w-max">
      {applicationText}
    </div>
  );

  const extendRowActions = (record: WorkaroundApplicationOverview) => {
    return (
      <button
        type="button"
        onClick={() =>
          navigate(
            `/cyber/location-level/${locationId}/application-workarounds/${record.uuid}`,
          )
        }
      >
        <RightOutlined />
      </button>
    );
  };

  return (
    <div>
      <TitleUpdater title="Location Application Workarounds" />
      <TitleBar
        title="Location Application Workarounds"
        subtitle={`${locationName} Overview`}
        brief="This provides an overview of the location level application workarounds. Visit any section below to add or edit information."
      />
      <QueryErrorBoundary error={[workaroundsError]}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          {cards}
        </div>
        <div className="mt-8">
          <TableV3
            columns={columns}
            data={tableData}
            loading={workaroundsLoading}
            useBorderedSection
            extendActions={extendRowActions}
            extendOptions={extendTableOptions}
            tableSize="small"
          />
        </div>
      </QueryErrorBoundary>
      <Footer backPath={`/cyber/location-level/${locationId}/options`} />
    </div>
  );
};

export default LocationLevelApplicationWorkarounds;
